import { format, parseISO, isBefore } from "date-fns";

import { TODAY, updateOverlayFromR2, isInSeason, isPastThreshold, calcSeasonYear, COLORS, fetchFromAcis, splitIntoYears } from "../../../utilities";

import knownCountiesWithBLD from './known-counties-with-bld.json';
import knownCountiesWithHWA from './known-counties-with-hwa.json';

export const TAB_NAME = 'Pests and Pathogens';

export function getInitTemp(subTab) {
  let newValue = null;
  if (subTab === 'Southern Pine Beetle') {
    newValue = -4;
  } else if (subTab === 'Pine Wilt Disease') {
    newValue = 5;
  } else if (subTab === 'Hemlock Wooly Adelgid MeanWT') {
    newValue = 23;
  } else if (subTab === 'Hemlock Wooly Adelgid MeanDT') {
    newValue = 14;
  } else if (subTab === 'Hemlock Wooly Adelgid MinT') {
    newValue = -40;
  } else if (subTab === 'Heterobasidion Root Disease') {
    newValue = -13;
  }
  return newValue;
}

function extractDateRange(startDate, endDate, dataArr) {
  let startIndex = dataArr.findIndex(dayArr => dayArr[0].slice(5) === startDate);
  let endIndex = dataArr.findIndex(dayArr => dayArr[0].slice(5) === endDate);

  if (startIndex === -1 && startDate === '02-29') {
    startIndex = dataArr.findIndex(dayArr => dayArr[0].slice(5) === '02-28');
  }

  if (endIndex === -1 && endDate === '02-29') {
    endIndex = dataArr.findIndex(dayArr => dayArr[0].slice(5) === '02-28');
  }

  if (startIndex === -1 || endIndex === -1) {
    throw new Error("Cannot find date in data array");
  }

  return dataArr.slice(startIndex, endIndex + 1);
}

function sumOnIndex(arr, idx) {
  if (arr.length === 0 || arr[0].length <= idx || idx < 0) {
    throw new Error("Empty data array or invalid index provided");
  }
  return arr.reduce((total, dayArr) => total + dayArr[idx], 0);
}

function calculateDefoliationSeverity(seasonPcpns, seasonTemps) {
  // Extract the relevant date range, sum the precip values, then convert the precip total from inches to mm
  const summerPcpnMM = convertInchToMM(sumOnIndex(extractDateRange('06-01', '08-31', seasonPcpns), 1));
  const springPcpnMM = convertInchToMM(sumOnIndex(extractDateRange('03-01', '05-31', seasonPcpns), 1));
  const mayPcpnMM = convertInchToMM(sumOnIndex(extractDateRange('05-01', '05-31', seasonPcpns), 1));

  // Extract the relevant date range, sum the temperature values, divide by number of days to get average, then convert the temperature average from °F to °C
  const winterMaxts = extractDateRange('12-01', '02-29', seasonTemps);
  const winterMaxtC = convertFtoC(sumOnIndex(winterMaxts, 2) / winterMaxts.length);
  const marchMints = extractDateRange('03-01', '03-31', seasonTemps);
  const marchMintC = convertFtoC(sumOnIndex(marchMints, 1) / marchMints.length);

  // Model that predicts White Pine Needle Damage as described in:
  //   "Emergence of white pine needle damage in the northeastern United States is associated with changes in pathogen pressure in response to climate change"
  //   Global Change Biology (2017) 23, 394–405, doi: 10.1111/gcb.13359
  return 0.08 + (0.0029 * summerPcpnMM) + (-0.0048 * springPcpnMM) + (0.0081 * mayPcpnMM) + (0.19 * winterMaxtC) + (-0.086 * marchMintC);
}

function calcWhitePineNeedleDamageChartData(pcpns, tempExtremes) {
  const newChartData = {
    years: [],
    defoliationSeverity: []
  };

  const seasonStartDay = '12-01';
  const seasonEndDay = '08-31';
  let idxYearStart = null;
  for (let i = 0; i < pcpns.length; i++) {
    const strDate = pcpns[i][0];

    if (strDate.slice(5) === seasonEndDay && idxYearStart !== null) {
      // "Year" should be the year in which the defoliation severity is observed, so the year after the observed data. Therefore, add one to the year.
      newChartData.years.push(parseInt(strDate.slice(0,4)) + 1);
      const defoliationSeverity = calculateDefoliationSeverity(pcpns.slice(idxYearStart, i + 1), tempExtremes.slice(idxYearStart, i + 1));
      newChartData.defoliationSeverity.push(defoliationSeverity);
    }
    
    if (strDate.slice(5) === seasonStartDay) {
      idxYearStart = i;
    }
  }

  return newChartData;
}

function getStandardDeviation(array) {
  if (array.length < 2) {
    return undefined;
  }
  const n = array.length;
  const mean = array.reduce((a, b) => a + b) / n;
  return Math.sqrt(
    array.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / (n - 1),
  );
}

function convertFtoC(val) {
  return (val - 32) * (5/9);
}

function convertInchToMM(val) {
  return val * 25.4;
}

function categorizeProbability(val, probsArray) {
  const colors = [
    '#800000',
    '#e80000',
    '#ff5500',
    '#ffaa00',
    '#ebff0c',
    '#a2ff56'
  ];
  
  const results = { y: 0, color: colors[colors.length - 1] };
  for (let i = 0; i < probsArray.length; i++) {
    const { low, high, probability } = probsArray[i];
    if (low <= val && val <= high) {
      results.y = probability;
      results.color = colors[i];
      break;
    }
  }
  return results;
}


const isothermalityProbs = [
  { low: 28.75, high: 29.25, probability: 85 },
  { low: 28.5, high: 29.5, probability: 75 },
  { low: 28.0, high: 30.5, probability: 50 },
  { low: 27.5, high: 32.5, probability: 25 },
  { low: 27.0, high: 33.5, probability: 10 }
];

const seasonalityProbs = [
  { low: 930, high: 935, probability: 85 },
  { low: 925, high: 940, probability: 75 },
  { low: 915, high: 955, probability: 50 },
  { low: 910, high: 965, probability: 25 },
  { low: 880, high: 970, probability: 10 }
];

function reformatAcis(acisArr, step='daily') {
  return acisArr.reduce((acc, [d, v]) => {
    acc.dates.push(step === 'daily' ? d.slice(5) : d.slice(0,4));
    acc.values.push(v);
    return acc;
  }, { dates: [], values: [] });
}

function calcHWAChartData(acisDataObjs, avgtThreshold, avgmintThreshold, mintThreshold) {
  return {
    dailyMints: { ...reformatAcis(acisDataObjs.dailyMint, 'daily'), threshold: typeof mintThreshold === 'number' ? mintThreshold : mintThreshold[0] },
    mintsByYear: { ...reformatAcis(acisDataObjs.mintsByYear, 'annual'), threshold: typeof avgmintThreshold === 'number' ? avgmintThreshold : avgmintThreshold[0] },
    annualAvgts: { ...reformatAcis(acisDataObjs.annualAvgt, 'annual'), threshold: typeof avgtThreshold === 'number' ? avgtThreshold : avgtThreshold[0] },
    annualMints: { ...reformatAcis(acisDataObjs.annualMint, 'annual'), threshold: typeof mintThreshold === 'number' ? mintThreshold : mintThreshold[0] },
    season: acisDataObjs.season
  };
}

function calcBeechLeafDiseaseChartData(acisData) {
  const results = {
    years: [],
    isothermalityProbabilities: [],
    seasonalityProbabilities: []
  };
  let yearData = { tempDiffs: [], avgts: [], mint: 999, maxt: -999 };
  for (const [date, mint, maxt, avgt] of acisData) {
    yearData.tempDiffs.push(maxt - mint);
    yearData.avgts.push(convertFtoC(avgt));
    if (mint < yearData.mint) yearData.mint = mint;
    if (maxt > yearData.maxt) yearData.maxt = maxt;
    
    if (date.slice(5,7) === '12') {
      // ISOTHERMALITY
      // Mean difference between monthly avg. mint and monthly avg. maxt for year
      const isothermalityNumerator = yearData.tempDiffs.reduce((sum, val) => sum + val, 0) / yearData.tempDiffs.length;
      // Warmest monthly maxt minus coldest monthly mint for year
      const isothermalityDenominator = yearData.maxt - yearData.mint;
      const isothermality = isothermalityNumerator/isothermalityDenominator * 100;
  
      // SEASONALITY
      // Standard deviation * 100 of the year's monthly mean avgt in °C.
      const seasonality = getStandardDeviation(yearData.avgts) * 100;

      // Add to results
      results.years.push(date.slice(0,4));
      results.isothermalityProbabilities.push(categorizeProbability(isothermality, isothermalityProbs));
      results.seasonalityProbabilities.push(categorizeProbability(seasonality, seasonalityProbs));
  
      // Reset year object
      yearData = { tempDiffs: [], avgts: [], mint: 999, maxt: -999 };
    }
  }

  return results;
}

const calcChartData = (temps, dataIdx, thresholds, seasonBounds, direction, TODAY) => {
  const compFn = isPastThreshold(thresholds, direction);
  const newChartData = {
    timeseries: {
      dates: [],
      current: []
    },
    yearlySeasons: {
      years: [],
      toDate: [],
      fullSeason: []
    }
  };
  
  const seasonStartDay = seasonBounds[0].slice(5,10);
  const seasonEndDay = seasonBounds[1].slice(5,10);
  const today = isInSeason(TODAY.toISOString(), seasonBounds[0], seasonBounds[1]) ? TODAY.toISOString().slice(0,10) : seasonBounds[1];
  newChartData.yearlySeasons.toDateDate = today.slice(0,10);
  
  let idxYearStart = null;
  let lastIdxProcessed = 0;
  const selectedSeasonIndices = [null, null];
  for (let i = 0; i < temps.length; i++) {
    const strDate = temps[i][0];
    const isToday = strDate.slice(5,10) === today.slice(5,10);

    if (strDate === seasonBounds[0]) selectedSeasonIndices[0] = i;
    if (strDate === seasonBounds[1]) selectedSeasonIndices[1] = i;

    if (isToday && idxYearStart !== null) {
      const seasonData = temps.slice(idxYearStart, i + 1);
      newChartData.yearlySeasons.toDate.push(seasonData.filter((dayData) => compFn(dayData[dataIdx])).length);
    }
    
    if (strDate.slice(5) === seasonEndDay && idxYearStart !== null) {
      const seasonData = temps.slice(idxYearStart, i + 1);
      const seasonYear = calcSeasonYear(strDate, seasonBounds[0], seasonBounds[1], false);
      newChartData.yearlySeasons.years.push(seasonYear);
      newChartData.yearlySeasons.fullSeason.push(seasonData.filter((dayData) => compFn(dayData[dataIdx])).length);
      lastIdxProcessed = i;
    }
    
    if (strDate.slice(5) === seasonStartDay) {
      idxYearStart = i;
    }
  }

  if (idxYearStart !== null && (lastIdxProcessed === null || idxYearStart > lastIdxProcessed)) {
    const currentSeasonData = temps.slice(idxYearStart);
    const seasonYear = calcSeasonYear(currentSeasonData[currentSeasonData.length - 1][0], seasonBounds[0], seasonBounds[1], false);

    newChartData.yearlySeasons.years.push(seasonYear);
    newChartData.yearlySeasons.fullSeason.push(null);
  }

  const selectedSeasonData = temps.slice(selectedSeasonIndices[0], selectedSeasonIndices[1] === null ? temps.length : selectedSeasonIndices[1] + 1);
  newChartData.timeseries = selectedSeasonData.reduce((acc, dayData) => {
    acc.dates.push(dayData[0]);
    acc.current.push(dayData[dataIdx]);
    return acc;
  }, { dates: [], current: [] });

  return newChartData;
};

export function updateChartData({ tabInformation, selectedTab, selectedSubTab, selectedLocation, tabsSharedState, rawChartData, pastLocations }, _, { setRawChartData, setChartData }) {
  if (
    selectedTab === TAB_NAME &&
    selectedLocation in rawChartData && 'mintMaxt' in rawChartData[selectedLocation] && 'pcpn' in rawChartData[selectedLocation]
  ) {
    let newChartData;
    if (selectedSubTab === 'Beech Leaf Disease') {
      const dataName = 'beechLeafDiseaseData';
      if (!(dataName in rawChartData[selectedLocation])) {
        const locInfo = pastLocations[selectedLocation];
        const coords = `${locInfo.lng},${locInfo.lat}`;

        // Start Jan. 1951, end December of last full year. Ignore current year
        // Gets monthly mean for each of mint, maxt, avgt
        const elems = {
          loc: coords,
          grid: 'nrcc-nn',
          sdate: '1951-01',
          edate: `${new Date().getFullYear() - 1}-12`,
          elems:[
            { name: "mint", interval: [0,1], duration: 1, reduce: "mean" },
            { name: "maxt", interval: [0,1], duration: 1, reduce: "mean" },
            { name: "avgt", interval: [0,1], duration: 1, reduce: "mean" }
          ]
        };

        return fetchFromAcis(elems)
          .then(rawData => {
            const newRawChartData = { ...rawChartData };
            newRawChartData[selectedLocation][dataName] = rawData;
            return () => setRawChartData(newRawChartData);
          });
      } else {
        newChartData = calcBeechLeafDiseaseChartData(rawChartData[selectedLocation][dataName]);
      }
    } else if (selectedSubTab === 'Hemlock Woolly Adelgid') {
      const dataName = 'hemlockWoollyAdelgidData';
      if (!(dataName in rawChartData[selectedLocation])) {
        const locInfo = pastLocations[selectedLocation];
        const coords = `${locInfo.lng},${locInfo.lat}`;

        const today = new Date();
        const todayMMDD = format(today, '-MM-dd');
        const thisMonth = today.getMonth();
        const isApril = thisMonth === 3;
        const isDecember = thisMonth === 11;
        const isActiveSeason = 11 <= thisMonth || thisMonth <= 2;
        const eYear = today.getFullYear();
        const avgtEndMMDD = isActiveSeason ? todayMMDD : '-03-31';
        const mintEndMMDD = (isActiveSeason || isApril) ? todayMMDD : '-04-30';

        const getDailyMint = fetchFromAcis({
          loc: coords,
          grid: 'nrcc-nn',
          sdate: '1950-12-01',
          edate: eYear + mintEndMMDD,
          elems:[{ name: "mint"}]
        });
        
        const lastCompleteYear = eYear - (2 < thisMonth ? 0 : 1);
        const getAnnualAvgt = fetchFromAcis({
          loc: coords,
          grid: 'nrcc-nn',
          sdate: '1951' + avgtEndMMDD,
          edate: lastCompleteYear + avgtEndMMDD,
          elems:[{"name":"avgt","interval":[1,0,0],"duration":"std","season_start":[12,1],"reduce":"mean"}]
        });

        const getAnnualMint = fetchFromAcis({
          loc: coords,
          grid: 'nrcc-nn',
          sdate: '1951' + mintEndMMDD,
          edate: lastCompleteYear + mintEndMMDD,
          elems:[{"name":"mint","interval":[1,0,0],"duration":"std","season_start":[11,1],"reduce":"min"}]
        });

        const season = isDecember ? `${today.getFullYear()} - ${today.getFullYear() + 1}` : `${today.getFullYear() - 1} - ${today.getFullYear()}`;

        return Promise.all([getDailyMint, getAnnualAvgt, getAnnualMint])
          .then(([dailyMint, annualAvgt, annualMint]) => {
            // Split daily data into years
            const dailyMintsByYear = splitIntoYears(dailyMint, '12-01', '04-30');

            const mintsByYear = dailyMintsByYear.map((dailyMints, idx) => {
              let lastDate = dailyMints[dailyMints.length - 1][0];
              const lastDateYear = parseInt(lastDate.slice(0,4));
              const finalSeasonYear = lastDate.slice(5) < '05-01' ? lastDateYear : lastDateYear + 1;
              console.log(dailyMints[0], dailyMints[dailyMints.length - 1], finalSeasonYear);
              return [String(finalSeasonYear), dailyMints.map(([_,v]) => v)];
            })

            const newRawChartData = { ...rawChartData };
            newRawChartData[selectedLocation][dataName] = { dailyMint: dailyMintsByYear[dailyMintsByYear.length - 1], mintsByYear, annualAvgt, annualMint, season };
            return () => setRawChartData(newRawChartData);
          });
      } else {
        const tab = tabInformation.find(({ name }) => name === TAB_NAME);
        const subTabChartOptions = tab.pageInfo[selectedSubTab].chart.options;
        const avgtThreshold = subTabChartOptions.find(({ id }) => id === 'hemlock-woolly-adelgid-meanwt-threshold-selector').props.value;
        const avgmintThreshold = subTabChartOptions.find(({ id }) => id === 'hemlock-woolly-adelgid-meandt-threshold-selector').props.value;
        const mintThreshold = subTabChartOptions.find(({ id }) => id === 'hemlock-woolly-adelgid-mint-threshold-selector').props.value;
        newChartData = calcHWAChartData(rawChartData[selectedLocation][dataName], avgtThreshold, avgmintThreshold, mintThreshold);
      }
    } else {
      let seasonBounds = tabsSharedState.pestsAndPathogensSeasonBoundsSelector.value;
      if (typeof seasonBounds === 'function') seasonBounds = seasonBounds();
      
      if (selectedSubTab === 'White Pine Needle Damage') {
        newChartData = calcWhitePineNeedleDamageChartData(rawChartData[selectedLocation]['pcpn'], rawChartData[selectedLocation]['mintMaxt']);
      } else {
        const reformattedName = selectedSubTab.split(' ').map(p => p.toLowerCase()).join('-') + '-threshold-selector';
        const tab = tabInformation.find(({ name }) => name === TAB_NAME);
        const subTabChartOptions = tab.pageInfo[selectedSubTab].chart.options;
        const threshold = subTabChartOptions.find(({ id }) => id === reformattedName).props.value;
        newChartData = calcChartData(rawChartData[selectedLocation]['mintMaxt'], 1, threshold, seasonBounds, 'below', TODAY);
        newChartData['threshold'] = threshold[0];
      }
    }

    return () => setChartData(newChartData);
  }
  return () => {};
}

export function updateOverlay({ biomassInfo, selectedSubTab, tabsSharedState }, _, { updateOverlayData }) {
  let isReversed = false;
  let binColors = undefined;
  let thresholdFn = undefined;
  let extraLegendInfo = { shiftLabels: -5 };
  let jsonFileDataKey = 'departures';
  const reformattedName = selectedSubTab.toLowerCase().split(' ').join('_');
  let jsonFileName = `${reformattedName}.json`;
  let countiesWithSpecies = [];

  if (selectedSubTab === 'White Pine Needle Damage') {
    jsonFileDataKey = 'data';
    thresholdFn = (i) => i + 1;
    binColors = [COLORS.darkRed, COLORS.orange, COLORS.yellow, COLORS.green];
    extraLegendInfo = {
      comp: 'lt',
      titleFn: (c) => 'Defoliation Severity',
      labelsFn: (c) => ['Severe', 'Moderate', 'Mild', 'Healthy'],
      isPercentage: false,
      shiftLabels: -36
    };
  } else if (selectedSubTab === 'Beech Leaf Disease') {
    const useKnownPresence = tabsSharedState['bldKnownCountiesToggle'].value;
    if (useKnownPresence) countiesWithSpecies = knownCountiesWithBLD;
    const variable = tabsSharedState['beechLeafDiseaseOverlayTypeSelector'].value;
    // const timeFrame = tabsSharedState['beechLeafDiseaseOverlayTimeframeSelector'].value;
    jsonFileDataKey = 'data';
    jsonFileName = `${variable.toLowerCase()}_${reformattedName}.json`;
    const thresholds = (variable === 'Isothermality' ? isothermalityProbs : seasonalityProbs).map(obj => obj.probability);
    thresholds.reverse();
    thresholdFn = (i) => thresholds[i];
    binColors = [
      'rgb(127.5,0.0,0.0)',
      'rgb(232.0,0.2,0.0)',
      'rgb(255.0,85.4,0.0)',
      'rgb(255.0,170.6,0.0)',
      'rgb(234.7,255.0,12.1)',
      'rgb(160.5,255.0,86.3)',
    ];
    extraLegendInfo = {
      comp: 'lt',
      isPercentage: true,
      titleFn: () => 'Probability of Disease',
      shiftLabels: -5
    };
  } else if (selectedSubTab === 'Hemlock Woolly Adelgid') {
    const useKnownPresence = tabsSharedState['hwaKnownCountiesToggle'].value;
    if (useKnownPresence) countiesWithSpecies = knownCountiesWithHWA;
    jsonFileDataKey = 'data';
    const overlayTypeName = tabsSharedState['hemlockWoollyAdelgidOverlayTypeSelector'].value;
    if (overlayTypeName === 'Current Year') {
      thresholdFn = (i) => i;
      binColors = [COLORS.darkRed, COLORS.green];
      extraLegendInfo = {
        isPercentage: false,
        comp: 'le',
        titleFn: () => 'Likelihood of Survival',
        labelsFn: () => ['High', 'Low'],
        shiftLabels: -40
      };
    } else {
      thresholdFn = (i) => (i + 1) * 5;
      binColors = [COLORS.darkRed, COLORS.lightRed, COLORS.orange, COLORS.yellow, COLORS.lightGreen, COLORS.darkGreen];
      extraLegendInfo = {
        isPercentage: false,
        comp: 'le',
        titleFn: () => 'Years with High Risk',
        shiftLabels: -5
      };
    }    
  } else if (tabsSharedState['departureSelector'].value.includes('(Days)')) {
    thresholdFn = (i) => (i - 2) * 5;
    extraLegendInfo = {
      isPercentage: false,
      shiftLabels: -5
    };
  } else if (tabsSharedState['departureSelector'].value === 'Current Year') {
    thresholdFn = (i) => i === 0 ? 1 : 0;
    binColors = [COLORS.darkRed, COLORS.green];
    extraLegendInfo = {
      isPercentage: false,
      comp: 'ge',
      titleFn: () => 'Likelihood of Damage',
      labelsFn: () => ['High', 'Low'],
      shiftLabels: -40
    };

    if (selectedSubTab === 'Pine Wilt Disease') {
      extraLegendInfo.titleFn = () => 'Likelihood of Survival'
    }
  }

  if (selectedSubTab === 'Southern Pine Beetle') {
    extraLegendInfo.titleFn = () => 'Likelihood of Damage';
    const mortalitySelection = tabsSharedState['spbMortalitySelector'].value;
    const temp = mortalitySelection.split(' ').slice(-1)[0].slice(0,-3);
    jsonFileDataKey = `${temp}_data`;
  }


  if (countiesWithSpecies.length === 0) {
    let biomassThreshold = tabsSharedState['biomassThresholdSelector'].value;
    if (typeof biomassThreshold === 'function') biomassThreshold = biomassThreshold(selectedSubTab);
    const maxBiomassByCounty = Object.entries(biomassInfo).find(([name,obj]) => jsonFileName.includes(name))[1];
    Object.keys(maxBiomassByCounty).forEach(countyFips => {
      if (biomassThreshold <= maxBiomassByCounty[countyFips]) {
        countiesWithSpecies.push(countyFips);
      }
    });
  }

  return updateOverlayFromR2(jsonFileName, jsonFileDataKey, binColors, isReversed, thresholdFn, extraLegendInfo, countiesWithSpecies)
    .then(d => () => updateOverlayData(d));
}

export function updateTitle({selectedSubTab, display, overlayDataDate, chartData, tabsSharedState}) {
  try {
    let sdate, edate, dateline;
    if (selectedSubTab === 'White Pine Needle Damage') {
      if (display === 'map') {
        const dateObj = parseISO(overlayDataDate);
        const isCurrentSeasonYear = isBefore(dateObj, new Date(dateObj.getFullYear(), 11, 1));
        let eyear = dateObj.getFullYear();
        let syear = eyear - (isCurrentSeasonYear ? 1 : 0);

        if (tabsSharedState.wpndOverlaySelector.value === 'Last 30 Years') {
          syear -= 30;
          eyear -= 1;
        }

        sdate = format(new Date(syear, 11, 1), 'LLL do, yyyy');
        edate = format(dateObj, 'LLL do, ') + String(eyear);
      } else {
        sdate = chartData.years[0];
        edate = chartData.years[chartData.years.length - 1];
      }
    } else if (selectedSubTab === 'Beech Leaf Disease') {
      if (display === 'map') {
        if (tabsSharedState.beechLeafDiseaseOverlayTimeframeSelector.value === 'Last Year') {
          edate = parseISO(overlayDataDate).getFullYear();
        } else {
          edate = parseISO(overlayDataDate).getFullYear();
          sdate = edate - 29;
        }
      } else {
        sdate = chartData.years[0];
        edate = chartData.years[chartData.years.length - 1];
      }
    } else if (selectedSubTab === 'Hemlock Woolly Adelgid') {
      if (display === 'map') {
        const isLast30 = tabsSharedState.hemlockWoollyAdelgidOverlayTypeSelector.value === 'Last 30 Years';
        const dateObj = parseISO(overlayDataDate);
        const isCurrentSeasonYear = isBefore(dateObj, new Date(dateObj.getFullYear(), 11, 1));
        let eyear = dateObj.getFullYear();
        let syear = eyear - (isCurrentSeasonYear ? 1 : 0);

        if (isLast30) {
          sdate = String(syear - 29);
          edate = String(eyear - 1);
        } else {
          sdate = format(new Date(syear, 11, 1), 'LLL do, yyyy');
          edate = format(dateObj, 'LLL do, ') + String(eyear);
        }
      } else {
        dateline = chartData.season;
      }
    } else {
      if (display === 'map') {
        const dateObj = parseISO(overlayDataDate);
        const isCurrentSeasonYear = isBefore(dateObj, new Date(dateObj.getFullYear(), 8, 1));
        let eyear = dateObj.getFullYear();
        let syear = eyear - (isCurrentSeasonYear ? 1 : 0);

        if (tabsSharedState.departureSelector.value.includes('Last Year')) {
          syear -= 1;
          eyear -= 1;
        } else if (tabsSharedState.departureSelector.value.includes('Normal')) {
          syear -= 30;
          eyear -= 1;
        }

        sdate = format(new Date(syear, 8, 1), 'LLL do, yyyy');
        edate = format(dateObj, 'LLL do, ') + String(eyear);
      } else {
        sdate = format(parseISO(chartData.timeseries.dates[0]), 'LLL do, yyyy');
        edate = format(parseISO(chartData.timeseries.dates.slice(-1)[0]), 'LLL do, yyyy');
      }
    }

    const type = selectedSubTab;
    return {
      type,
      sdate,
      edate,
      dateline
    }
  } catch {
    return null;
  }
}